import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { BarChart3, Users, FileText, Calculator, ShoppingCart, Library as LibIcon, Images, Sparkles } from "lucide-react";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-zinc-200 rounded-lg p-5 bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-500">
        <Icon size={14} />
        <p className="text-[10px] uppercase tracking-[0.25em] font-medium">{label}</p>
      </div>
      <p className="mt-3 font-heading text-4xl font-bold tracking-tight">{value}</p>
    </div>
  );
}

function BarList({ title, rows, max, keyName = "name", testid }) {
  const top = Math.max(1, max || Math.max(...rows.map((r) => r.count), 1));
  return (
    <div className="border border-zinc-200 rounded-lg p-5 bg-white" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-medium mb-4">{title}</p>
      {rows.length === 0 && <p className="text-xs text-zinc-400">No data yet.</p>}
      <div className="space-y-2.5">
        {rows.map((r, i) => {
          const label = r[keyName] || r.tool || r.user || r.status || r.type;
          const pct = Math.round((r.count / top) * 100);
          return (
            <div key={i}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="truncate pr-2 capitalize">{String(label).replace(/_/g, " ")}</span>
                <span className="text-zinc-500 font-medium tabular-nums">{r.count}</span>
              </div>
              <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-zinc-950 rounded-full" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/analytics/usage").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (!data) return <div className="p-10 text-sm text-zinc-400">Loading analytics…</div>;

  const t = data.totals || {};

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Admin</p>
        <h1 className="font-heading text-4xl font-bold mt-1 tracking-tight" data-testid="analytics-title">Usage analytics</h1>
        <p className="text-zinc-500 mt-2 text-sm max-w-xl">Team-wide activity, most active designers and where AI generations are going each week.</p>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="analytics-totals">
        <Stat icon={Sparkles} label="Generations" value={t.generations || 0} testid="stat-generations" />
        <Stat icon={Images} label="Designs" value={t.designs || 0} testid="stat-designs" />
        <Stat icon={FileText} label="Techpacks" value={t.techpacks || 0} testid="stat-techpacks" />
        <Stat icon={Calculator} label="Cost sheets" value={t.cost_sheets || 0} testid="stat-costs" />
        <Stat icon={ShoppingCart} label="Purchase orders" value={t.purchase_orders || 0} testid="stat-pos" />
        <Stat icon={LibIcon} label="Library items" value={t.library_items || 0} testid="stat-libs" />
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <BarList title="Generations by tool" rows={data.generations_by_tool || []} keyName="tool" testid="chart-by-tool" />
        <BarList title="Top designers (by generations)" rows={data.generations_by_user || []} keyName="user" testid="chart-by-user" />
        <BarList title="Techpack status" rows={data.techpack_status || []} keyName="status" testid="chart-status" />
        <BarList title="Library items by type" rows={data.library_by_type || []} keyName="type" testid="chart-lib-type" />
        <div className="md:col-span-2">
          <BarList title="Most-used materials (from techpack BOMs)" rows={data.top_materials || []} keyName="name" testid="chart-top-materials" />
        </div>
      </div>
    </div>
  );
}
